import { TemplateTypeDetector } from 'dashboard/services/TemplateTypeDetector';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  PLATFORMS,
  TEMPLATE_TYPES,
} from 'dashboard/services/TemplateConstants';

const TEMPLATE_TYPE_KEYS = {
  [TEMPLATE_TYPES.WHATSAPP_TEXT]: 'TEXT',
  [TEMPLATE_TYPES.WHATSAPP_TEXT_HEADER]: 'TEXT',
  [TEMPLATE_TYPES.WHATSAPP_MEDIA_IMAGE]: 'IMAGE',
  [TEMPLATE_TYPES.WHATSAPP_MEDIA_VIDEO]: 'VIDEO',
  [TEMPLATE_TYPES.WHATSAPP_MEDIA_DOCUMENT]: 'DOCUMENT',
  [TEMPLATE_TYPES.WHATSAPP_INTERACTIVE]: 'CALL_TO_ACTION',
  [TEMPLATE_TYPES.WHATSAPP_COPY_CODE]: 'COPY_CODE',
  [TEMPLATE_TYPES.TWILIO_TEXT]: 'TEXT',
  [TEMPLATE_TYPES.TWILIO_MEDIA]: 'MEDIA',
  [TEMPLATE_TYPES.TWILIO_QUICK_REPLY]: 'QUICK_REPLY',
  [TEMPLATE_TYPES.TWILIO_CALL_TO_ACTION]: 'CALL_TO_ACTION',
  [TEMPLATE_TYPES.TWILIO_CARD]: 'CATALOG',
};

export const groupTemplates = templateRecords => {
  const groupedTemplates = new Map();

  templateRecords.forEach(({ template, inbox, lastUpdatedAt }) => {
    const platform =
      inbox.channel_type === INBOX_TYPES.TWILIO
        ? PLATFORMS.TWILIO
        : PLATFORMS.WHATSAPP;
    const providerAccountId =
      inbox.provider_config?.business_account_id ||
      inbox.account_sid ||
      inbox.id;
    const name = template.name || template.friendly_name;
    const providerTemplateIdentifier = template.id || template.content_sid;
    const templateIdentifier = providerTemplateIdentifier || name;
    const key = JSON.stringify(
      providerTemplateIdentifier
        ? [
            platform,
            providerAccountId,
            providerTemplateIdentifier,
            template.language,
          ]
        : [platform, inbox.id, name, template.language, template]
    );
    const searchableContent = JSON.stringify(
      template.components || template.types || template.body || []
    );
    const normalizedTemplate = {
      ...template,
      id: templateIdentifier,
      name,
      platform,
      key,
      inboxes: [inbox],
      inboxNames: inbox.name,
      lastUpdatedAt,
      searchableContent,
    };
    const existingTemplate = groupedTemplates.get(key);

    if (existingTemplate) {
      const inboxes = [...existingTemplate.inboxes, inbox];
      const incomingIsNewer =
        lastUpdatedAt &&
        (!existingTemplate.lastUpdatedAt ||
          new Date(lastUpdatedAt) > new Date(existingTemplate.lastUpdatedAt));

      groupedTemplates.set(key, {
        ...(incomingIsNewer ? normalizedTemplate : existingTemplate),
        inboxes,
        inboxNames: inboxes.map(item => item.name).join(', '),
        lastUpdatedAt: incomingIsNewer
          ? lastUpdatedAt
          : existingTemplate.lastUpdatedAt,
      });
      return;
    }

    groupedTemplates.set(key, normalizedTemplate);
  });

  return [...groupedTemplates.values()].sort((first, second) =>
    first.name.localeCompare(second.name)
  );
};

export const formatTemplateLabel = value => {
  if (!value) return '—';

  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
};

export const formatTemplateLanguage = language => {
  if (!language) return '—';

  const locale = language.replace('_', '-');
  const languageCode = locale.split('-')[0];
  const displayName = new Intl.DisplayNames([locale], {
    type: 'language',
  }).of(languageCode);

  return `${displayName} (${locale})`;
};

export const formatTemplateDate = value => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(new Date(value));
};

export const templateStatusClasses = status => {
  const classes = {
    approved: 'bg-n-teal-3 text-n-teal-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    rejected: 'bg-n-ruby-3 text-n-ruby-11',
    paused: 'bg-n-amber-3 text-n-amber-11',
    disabled: 'bg-n-alpha-2 text-n-slate-11',
  };

  return classes[status?.toLowerCase()] || 'bg-n-alpha-2 text-n-slate-11';
};

export const templateTypeKey = template => {
  const type =
    template.platform === PLATFORMS.TWILIO
      ? TemplateTypeDetector.detectTwilioType(template)
      : TemplateTypeDetector.detectWhatsAppType(template);
  return TEMPLATE_TYPE_KEYS[type] || 'TEXT';
};

const WHATSAPP_CLOUD_PROVIDER = 'whatsapp_cloud';
const EDITABLE_BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];

export const isCloudInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP &&
  inbox.provider === WHATSAPP_CLOUD_PROVIDER;

const findComponent = (template, type) =>
  template.components?.find(component => component.type === type);

// The form only covers text headers and the three basic button types;
// saving anything else through it would silently drop those parts.
export const isEditableTemplate = template => {
  const header = findComponent(template, 'HEADER');
  const buttons = findComponent(template, 'BUTTONS')?.buttons || [];
  return (
    (!header || header.format === 'TEXT') &&
    buttons.every(button => EDITABLE_BUTTON_TYPES.includes(button.type))
  );
};

export const templateVariables = text => [
  ...new Set((text || '').match(/\{\{\d+\}\}/g) || []),
];

export const emptyTemplateForm = () => ({
  inboxId: '',
  name: '',
  language: 'he',
  category: 'MARKETING',
  header: '',
  headerExample: '',
  body: '',
  bodyExamples: [],
  footer: '',
  buttons: [],
});

export const templateToForm = template => {
  const header = findComponent(template, 'HEADER');
  const body = findComponent(template, 'BODY');
  return {
    inboxId: template.inboxes[0].id,
    name: template.name,
    language: template.language,
    category: template.category,
    header: header?.text || '',
    headerExample: header?.example?.header_text?.[0] || '',
    body: body?.text || '',
    bodyExamples: [...(body?.example?.body_text?.[0] || [])],
    footer: findComponent(template, 'FOOTER')?.text || '',
    buttons: (findComponent(template, 'BUTTONS')?.buttons || []).map(
      button => ({
        type: button.type,
        text: button.text,
        value: button.url || button.phone_number || '',
      })
    ),
  };
};

const BUTTON_VALUE_KEYS = { URL: 'url', PHONE_NUMBER: 'phone_number' };

// body_text is sent flat; the API wraps it into Meta's nested shape.
export const buildTemplateComponents = form => {
  const components = [];
  if (form.header) {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: form.header,
      ...(templateVariables(form.header).length && {
        example: { header_text: [form.headerExample] },
      }),
    });
  }
  const bodyVariableCount = templateVariables(form.body).length;
  components.push({
    type: 'BODY',
    text: form.body,
    ...(bodyVariableCount && {
      example: { body_text: form.bodyExamples.slice(0, bodyVariableCount) },
    }),
  });
  if (form.footer) components.push({ type: 'FOOTER', text: form.footer });
  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(({ type, text, value }) => ({
        type,
        text,
        ...(BUTTON_VALUE_KEYS[type] && { [BUTTON_VALUE_KEYS[type]]: value }),
      })),
    });
  }
  return components;
};
